import {
  CARD_MAX_GROUPS,
  CARD_PERIODS,
  CARD_SCHEMA,
  REACTION_KINDS,
  WINE_TYPES,
  audienceIssues,
  cardContentIssues,
  isPeriodKey,
} from "@proef/domain";
import { z } from "zod";
import { PublicUser } from "./friendship.js";

export const CardPeriod = z.enum(CARD_PERIODS);
export type CardPeriod = z.infer<typeof CardPeriod>;

export const ReactionKind = z.enum(REACTION_KINDS);
export type ReactionKind = z.infer<typeof ReactionKind>;

const Count = z.int().nonnegative();
const Code = z.string().min(1).max(40);

/** One lexicon entry and how often it was tasted. Strict: a card holds codes and numbers only, so a
 * key nobody asked for is refused instead of quietly stored. */
const Tally = z.strictObject({ code: Code, count: Count, isNew: z.boolean() });

/**
 * What a card says: counts, wine types and lexicon codes. Never note text. Beyond the shape, the
 * domain's rules apply (the numbers add up, every code is in the lexicon), so the edge refuses
 * what no honest device would send.
 */
export const CardContent = z
  .strictObject({
    schema: z.literal(CARD_SCHEMA),
    counts: z.strictObject({ wines: Count, notes: Count, sessions: Count }),
    wineTypes: z
      .array(z.strictObject({ type: z.enum(WINE_TYPES), count: Count }))
      .max(WINE_TYPES.length),
    regions: z.array(Tally).max(50),
    countries: z.array(Tally).max(50),
    grapes: z.array(Tally).max(50),
    blind: z.strictObject({ calls: Count, correct: Count }),
    streakWeeks: Count,
    badges: z.array(z.string().min(1).max(40)).max(20),
  })
  .superRefine((card, ctx) => {
    for (const message of cardContentIssues(card)) ctx.addIssue({ code: "custom", message });
  });
export type CardContent = z.infer<typeof CardContent>;

/** Who sees a card: accepted friends, and the active members of the listed groups. */
export const CardAudience = z
  .strictObject({
    friends: z.boolean(),
    groupIds: z.array(z.string().min(1).max(100)).max(CARD_MAX_GROUPS + 1),
  })
  .superRefine((audience, ctx) => {
    for (const message of audienceIssues(audience)) ctx.addIssue({ code: "custom", message });
  });
export type CardAudience = z.infer<typeof CardAudience>;

/** PUT /v1/cards/:period/:key -- the same card again replaces what was sent for the period. */
export const PutCardRequest = z.strictObject({ content: CardContent, audience: CardAudience });
export type PutCardRequest = z.infer<typeof PutCardRequest>;

/** PUT /v1/cards/:id/reaction -- one reaction per person and card; a new one replaces the old. */
export const PutReactionRequest = z.strictObject({ kind: ReactionKind });
export type PutReactionRequest = z.infer<typeof PutReactionRequest>;

export const ReactionCounts = z.partialRecord(ReactionKind, z.int().positive());
export type ReactionCounts = z.infer<typeof ReactionCounts>;

/**
 * A card as its viewer sees it. `audience` is set on the viewer's own cards only; `reactions` on
 * the owner's own card counts every audience's reactions, on someone else's the viewer's row.
 */
export const CardView = z
  .object({
    id: z.string().min(1),
    period: CardPeriod,
    periodKey: z.string().min(1).max(10),
    content: CardContent,
    user: PublicUser,
    audience: CardAudience.nullable(),
    reactions: ReactionCounts,
    /** The viewer's own reaction; always null on their own card, which they cannot react to. */
    myReaction: ReactionKind.nullable(),
    sharedAt: z.iso.datetime(),
  })
  .refine((card) => isPeriodKey(card.period, card.periodKey), {
    message: "periodKey is not a key of that period",
    path: ["periodKey"],
  });
export type CardView = z.infer<typeof CardView>;

/** GET /v1/users/:id/cards -- newest first; the viewer's own include who sees each. */
export const CardList = z.object({ items: z.array(CardView) });
export type CardList = z.infer<typeof CardList>;
